/**
 * Traducción de nombres de FontAwesome (panel anterior) a iconos lucide.
 *
 * Varias fuentes devuelven el icono con el nombre de FontAwesome: las categorías
 * del FAQ (`faq_categorias.icono`), las sugerencias del asistente, etc. Aquí se
 * centraliza el mapeo para no repetir tablas en cada módulo.
 *
 * Cualquier nombre desconocido cae en un icono por defecto, así un dato mal
 * cargado nunca rompe la interfaz.
 */

import {
  ChartBar,
  ChartLine,
  ChartPie,
  CircleQuestionMark,
  Clock,
  Coins,
  Droplets,
  FileClock,
  FileText,
  Fuel,
  Gift,
  Headset,
  Info,
  Layers,
  Package,
  Settings,
  ShoppingCart,
  Tags,
  Target,
  TrendingUp,
  TriangleAlert,
  Truck,
  UserCog,
  Users,
  Wallet,
  Wrench,
  type LucideIcon,
} from 'lucide-react';

import { DEFAULT_MODULE_ICON, resolveModuleIcon } from '@/features/modulos/lib/module-icons';

const ICONS: Record<string, LucideIcon> = {
  'fa-solid fa-file-invoice': FileText,
  'fa-solid fa-file-invoice-dollar': FileText,
  'fa-solid fa-file-lines': FileText,
  'fa-solid fa-file': FileText,
  'fa-solid fa-file-alt': FileText,
  'fa-solid fa-gas-pump': Fuel,
  'fa-solid fa-gear': Settings,
  'fa-solid fa-cog': Settings,
  'fa-solid fa-chart-bar': ChartBar,
  'fa-solid fa-chart-column': ChartBar,
  'fa-solid fa-chart-line': ChartLine,
  'fa-solid fa-chart-pie': ChartPie,
  'fa-solid fa-triangle-exclamation': TriangleAlert,
  'fa-solid fa-circle-exclamation': TriangleAlert,
  'fa-solid fa-circle-question': CircleQuestionMark,
  'fa-solid fa-clock': Clock,
  'fa-solid fa-clock-rotate-left': FileClock,
  'fa-solid fa-headset': Headset,
  'fa-solid fa-tint': Droplets,
  'fa-solid fa-money-bill-trend-up': TrendingUp,
  'fa-solid fa-users': Users,
  'fa-solid fa-user-shield': UserCog,
  'fa-solid fa-cart-shopping': ShoppingCart,
  'fa-solid fa-boxes-stacked': Package,
  'fa-solid fa-box': Package,
  'fa-solid fa-wrench': Wrench,
  'fa-solid fa-toolbox': Wrench,
  'fa-solid fa-tags': Tags,
  'fa-solid fa-tag': Tags,
  'fa-solid fa-bullseye': Target,
  'fa-solid fa-gift': Gift,
  'fa-solid fa-coins': Coins,
  'fa-solid fa-wallet': Wallet,
  'fa-solid fa-truck': Truck,
  'fa-solid fa-info-circle': Info,
  'fa-solid fa-circle-info': Info,
};

export const DEFAULT_FA_ICON = Layers;

/** Quita el prefijo de estilo (`fa-solid fa-…`) para quedarnos con el nombre base. */
function nombreBase(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/^fa[srlbd]?\s+/, '')
    .replace(/^fa-/, '');
}

export function resolveFaIcon(name: string | null | undefined): LucideIcon {
  if (!name) return DEFAULT_FA_ICON;

  const key = name.trim().toLowerCase();
  const directo = ICONS[key] ?? ICONS[`fa-solid fa-${nombreBase(key)}`];
  if (directo) return directo;

  // Respaldo: el registro kebab-case del catálogo de módulos.
  const alterno = resolveModuleIcon(nombreBase(key));

  return alterno === DEFAULT_MODULE_ICON ? DEFAULT_FA_ICON : alterno;
}
